import { FC, lazy, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PageSpinner } from 'src/components/spinner-container';
import { isNetworkError } from 'src/hooks/use-axios';
import { useTso } from 'src/pages/market-information/players/operators-detail/use-tso';
import { useOperator } from 'src/pages/operators/assignments/details/components/use-operator';
import { useContractManagement } from 'src/pages/operators/assignments/details/use-contract-management';
import { monolithAssignmentEndpoints } from 'src/pages/operators/assignments/overview';
import { useGetAssignmentWithDocuments } from 'src/pages/operators/assignments/overview/use-get-assignment-with-documents';
import { useAssignmentsGroupedByOrganisation } from 'src/pages/operators/assignments/overview/useAssignmentsGroupedByOrganisation';
import { useTsoPlatformIds } from 'src/pages/operators/assignments/use-operator-platform-ids';
import { useGetOldUserIdDeprecated } from 'src/pages/settings/organisation/users/details/use-get-old-user-id';
import { useShipperUser } from 'src/pages/shipper-admin/details/use-shipper-user';
import {
  useShipperRegistration,
  useShipperRegistrationPublic,
} from 'src/pages/shippers/useShipperRegistration';

const Page = lazy(() => import('./page'));

type Props = {
  orgId: string;
  userId: string;
};

export const AssignmentsDetailsWithOldId: FC<Props> = ({ orgId, userId }) => {
  const tso = useTso(orgId);
  const contractManagement = useContractManagement();
  const shipperRegistrationPublic = useShipperRegistrationPublic();
  const shipperRegistration = useShipperRegistration();
  const assignmentsByOperator = useAssignmentsGroupedByOrganisation({
    url: monolithAssignmentEndpoints.getAssignmentsOfOrganisation,
  });
  const assignmentWithDocuments = useGetAssignmentWithDocuments({
    tsoId: orgId,
    userId,
  });
  const shipperUser = useShipperUser(userId);
  const operator = useOperator({ tso });

  if (
    !tso.response ||
    !shipperUser.response ||
    !shipperRegistrationPublic.response ||
    !shipperRegistration.response ||
    !assignmentsByOperator.response ||
    !assignmentWithDocuments.response ||
    (operator && !operator.loaded) ||
    (!contractManagement.response && !isNetworkError(contractManagement.error))
  ) {
    return <PageSpinner />;
  }

  return (
    <Page
      tsoId={orgId}
      userId={userId}
      tso={tso}
      shipperUser={shipperUser}
      contractManagement={contractManagement}
      shipperRegistrationPublic={shipperRegistrationPublic}
      shipperRegistration={shipperRegistration}
      assignmentsByOperator={assignmentsByOperator}
      assignmentWithDocuments={assignmentWithDocuments}
      caMaOperator={operator}
    />
  );
};

export const AssigmentDetails = () => {
  const { operatorUuid, userUuid } = useParams<{
    operatorUuid: string;
    userUuid: string;
  }>();

  const orgId = useTsoPlatformIds(operatorUuid);
  const getOldUserId = useGetOldUserIdDeprecated(userUuid);

  useEffect(() => {
    getOldUserId.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!getOldUserId.response || !orgId.response) return <PageSpinner />;

  return (
    <AssignmentsDetailsWithOldId
      orgId={orgId.response.data.referenceId.toString()}
      userId={getOldUserId.response.data.customerUserId.toString()}
    />
  );
};
